import { getIsCountryCompliant } from '@src/modules/global-state/locale-config.browser'

import { USER_CONSENT_COOKIES } from './constants'
import getCookie from './getCookie'

/**
 * Single source of truth for the `x-personalisation-enabled` BFF/SAND header
 * and its companion checks across SSR, the React hook, and the HTTP-client
 * request interceptors.
 *
 * Locale-aware consent gate, mirroring the same `countryCompliant + cookie`
 * pattern used by:
 *   - src/pages/_document.page.tsx          (3P script suppression)
 *   - src/telemetry/telemetry-registry.ts   (per-pixel firing)
 *   - src/quince-site-common/src/trackers/initializeAnalytics.ts (Mixpanel SDK init)
 *
 * Truth table:
 *   countryCompliant=true  (UK / GDPR)  → strict opt-in: allowed only when
 *                                          user_functional_consent === "true"
 *   countryCompliant=false (US / CA)    → opt-out: allowed unless cookie === "false"
 *
 * Pre-consent on a countryCompliant locale (cookie absent — TrustArc has not
 * been interacted with yet, including the very first SSR pass) resolves to
 * `false`, as required by UK PECR / GDPR. The legacy "absent → true" default
 * is preserved for non-compliant locales where TrustArc never fires.
 *
 * `isCountryCompliant` is passed in by the caller from the Statsig-resolved
 * locale config (`getServerSideLocaleConfig`) — NOT re-derived from the static
 * `getLocaleConfig` default — so this matches the dynamic value used everywhere
 * else (pageProps / feature-gate memory).
 */
export function isPersonalisationAllowed(
  isCountryCompliant: boolean,
  consentCookieValue: string | undefined,
): boolean {
  if (isCountryCompliant) {
    return consentCookieValue === 'true'
  }
  return consentCookieValue !== 'false'
}

/**
 * Browser-only: reads countryCompliant from the Statsig-resolved locale config
 * in memory (hydrated from pageProps) via getIsCountryCompliant().
 */
export function getPersonalisationAllowedFromCookies(): boolean {
  const consent = getCookie(USER_CONSENT_COOKIES.FUNCTIONAL)
  if (getIsCountryCompliant()) {
    return consent === 'true'
  }
  return consent !== 'false'
}
